import { memo, useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDropzone } from 'react-dropzone';
import { BiUpload } from '@react-icons/all-files/bi/BiUpload';
import { useFormContext } from 'react-hook-form';
import Image from 'next/image';

interface InputUploadHookFormProps {
  name: string;
  image?: string;
}

export const InputUploadHookForm = memo<InputUploadHookFormProps>(
  function InputUploadHookForm({ name, image }) {
    const { setValue, register } = useFormContext();

    const [file, setFile] = useState<any>(null);
    const imageSize = 143;

    const { getRootProps, getInputProps } = useDropzone({
      maxFiles: 1,
      accept: {
        'image/png': ['.png'],
        'image/jpeg': ['.jpeg', '.jpg'],
      },
      onDrop: (acceptedFiles) => {
        setFile(
          Object.assign(acceptedFiles[0], {
            preview: URL.createObjectURL(acceptedFiles[0]),
          })
        );

        setValue(name, acceptedFiles[0]);
      },
    });

    useEffect(() => {
      if (!file) return;

      // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
      return () => URL.revokeObjectURL(file.preview);
    }, [file]);

    return (
      <Box display="flex" gap={1}>
        <aside
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}
        >
          {file ? (
            <div
              style={{
                display: 'inline-flex',
                borderRadius: 4,
                width: imageSize,
                height: imageSize,
                padding: 2,
                boxSizing: 'border-box',
              }}
              key={file.name}
            >
              <div
                style={{
                  display: 'flex',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <Image
                  src={file.preview}
                  alt="Logo do seu time"
                  width={100}
                  height={100}
                  style={{
                    display: 'block',
                    width: 'auto',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                  // Revoke data uri after image is loaded
                  onLoad={() => {
                    URL.revokeObjectURL(file.preview);
                  }}
                />
              </div>
            </div>
          ) : (
            <Avatar
              variant="rounded"
              src={image ? image : 'images/fallback-image.png'}
              sx={(theme) => ({
                width: imageSize,
                height: imageSize,
                border: `1px solid ${theme.palette.grey[200]}`,
              })}
            />
          )}
        </aside>

        <Box
          width="100%"
          borderRadius={2}
          border="1px dashed #A0A0A0"
          p={2}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          gap={1}
          {...getRootProps({ className: 'dropzone' })}
        >
          <input ref={register as any} name={name} {...getInputProps()} />

          <Box
            display="flex"
            justifyContent="center"
            gap={1}
            alignItems="center"
            borderRadius={2}
            sx={(theme) => ({
              border: `1px solid ${theme.palette.grey[200]}`,
              padding: '8px',
            })}
          >
            <BiUpload size={24} />
            <Typography fontWeight={600} variant="body2">
              Enviar logo tipo
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography fontWeight={600} variant="caption">
              Clique para carregar ou arraste e solte
            </Typography>
            <Typography variant="caption" textAlign="center">
              PNG ou JPG com até 30mb (250px por 250px ou superior)
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);
